import { RefObject, useEffect, useRef } from 'react';

import LoadingSpinner from '@paradigm/design-system/src/components/LoadingSpinner';

interface Props {
  readonly disabled: boolean;
  readonly isLoading: boolean;
  readonly scrollableRef: RefObject<HTMLElement>;
  readonly onFetchMore: () => void;
}

export default function TableBottomLoader(props: Props) {
  const { disabled, isLoading, scrollableRef, onFetchMore } = props;
  const canFetchMore = !disabled && !isLoading;

  const elRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver>();
  const fetchMoreRef = useRef(noop);

  useEffect(() => {
    fetchMoreRef.current = onFetchMore;
  }, [onFetchMore]);

  useEffect(() => {
    const element = elRef.current;
    if (element == null) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        // entry can't be undefined since entries are never empty
        if (entry!.isIntersecting) {
          fetchMoreRef.current();
          observer.disconnect();
        }
      },
      {
        root: scrollableRef.current,
        rootMargin: '0px 0px 200px 0px',
      },
    );
    observerRef.current = observer;

    return () => observer.disconnect();
  }, [scrollableRef]);

  useEffect(() => {
    // Set to observe only when can fetch more
    const observer = observerRef.current;
    const element = elRef.current;
    if (observer == null) return;

    if (canFetchMore && element != null) {
      observer.observe(element);
    } else {
      observer.disconnect();
    }
  }, [canFetchMore]);

  return (
    <div ref={elRef} css="display:grid; place-items: center; padding: 1rem">
      {isLoading && <LoadingSpinner size={40} alt="loading spinner" />}
    </div>
  );
}

function noop() {
  /* noop */
}
